import React, { useState, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { XIcon, ArrowRightIcon, CheckCircleIcon, LinkedinIcon, BriefcaseIcon, StarIcon } from 'lucide-react';
const mentors = [{
  initials: 'SC',
  name: 'Sarah Chen',
  role: 'Senior Product Designer',
  company: 'Google',
  expertise: ['Design Systems', 'Figma', 'UX Research'],
  students: 48,
  rating: 4.9,
  bio: 'Led design for Google Maps and Google Pay. Passionate about helping designers break into top-tier companies.',
  color: 'from-blue-500/20 to-blue-600/10',
  accent: 'bg-blue-500/20 text-blue-300'
}, {
  initials: 'MJ',
  name: 'Marcus Johnson',
  role: 'Design Lead',
  company: 'Spotify',
  expertise: ['Motion Design', 'Framer', 'Brand Identity'],
  students: 36,
  rating: 5.0,
  bio: "Crafted Spotify's visual identity across 180+ markets. Expert in motion design and brand storytelling.",
  color: 'from-green-500/20 to-green-600/10',
  accent: 'bg-green-500/20 text-green-300'
}, {
  initials: 'AP',
  name: 'Aisha Patel',
  role: 'Product Designer',
  company: 'Meta',
  expertise: ['AI Design', 'Prototyping', 'User Research'],
  students: 52,
  rating: 4.8,
  bio: 'Designing AI-powered experiences at Meta Reality Labs. Specializes in emerging tech and interaction design.',
  color: 'from-purple-500/20 to-purple-600/10',
  accent: 'bg-purple-500/20 text-purple-300'
}, {
  initials: 'DK',
  name: 'David Kim',
  role: 'Head of Design',
  company: 'Stripe',
  expertise: ['Product Strategy', 'Design Ops', 'Leadership'],
  students: 29,
  rating: 4.9,
  bio: "Built Stripe's design team from 3 to 40+ designers. Focused on design leadership and career growth.",
  color: 'from-indigo-500/20 to-indigo-600/10',
  accent: 'bg-indigo-500/20 text-indigo-300'
}, {
  initials: 'ER',
  name: 'Emily Rodriguez',
  role: 'Design Lead',
  company: 'Airbnb',
  expertise: ['Service Design', 'Accessibility', 'Figma'],
  students: 41,
  rating: 4.9,
  bio: "Redesigned Airbnb's host onboarding experience. Champion of accessible and inclusive design practices.",
  color: 'from-rose-500/20 to-rose-600/10',
  accent: 'bg-rose-500/20 text-rose-300'
}, {
  initials: 'JW',
  name: 'James Wilson',
  role: 'UX Designer',
  company: 'Netflix',
  expertise: ['Content Design', 'A/B Testing', 'Data-Driven UX'],
  students: 33,
  rating: 4.8,
  bio: "Optimized Netflix's recommendation UI reaching 230M+ subscribers. Expert in data-driven design decisions.",
  color: 'from-red-500/20 to-red-600/10',
  accent: 'bg-red-500/20 text-red-300'
}];
type FormState = 'idle' | 'submitting' | 'success';
function ApplyModal({
  onClose
}: {
  onClose: () => void;
}) {
  const [formState, setFormState] = useState<FormState>('idle');
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    linkedin: '',
    expertise: '',
    motivation: ''
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormState('submitting');
    setTimeout(() => setFormState('success'), 1500);
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={e => e.target === e.currentTarget && onClose()} data-id="element-259">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.3
    }} className="relative w-full max-w-lg bg-[#111111] border border-white/10 rounded-2xl overflow-hidden max-h-[90vh] overflow-y-auto" data-id="element-260">
        {/* Header */}
        <div className="sticky top-0 bg-[#111111] border-b border-white/5 px-6 py-5 flex items-center justify-between z-10" data-id="element-261">
          <div data-id="element-262">
            <h3 className="text-xl font-bold text-white" data-id="element-263">Become a Mentor</h3>
            <p className="text-sm text-gray-400 mt-0.5" data-id="element-264">
              Share your expertise. Shape the next generation.
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-colors" data-id="element-265">
            <XIcon className="w-4 h-4" data-id="element-266" />
          </button>
        </div>

        <div className="px-6 py-6" data-id="element-267">
          <AnimatePresence mode="wait" data-id="element-268">
            {formState === 'success' ? <motion.div key="success" initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="text-center py-10" data-id="element-269">
                <div className="w-16 h-16 rounded-full bg-brand-yellow/20 flex items-center justify-center mx-auto mb-4" data-id="element-270">
                  <CheckCircleIcon className="w-8 h-8 text-brand-yellow" data-id="element-271" />
                </div>
                <h4 className="text-xl font-bold text-white mb-2" data-id="element-272">
                  Application Received!
                </h4>
                <p className="text-gray-400 mb-6" data-id="element-273">
                  Thanks for applying! Our team will review your application and
                  get back to you within 3–5 business days.
                </p>
                <button onClick={onClose} className="px-6 py-3 bg-brand-yellow text-dark-900 font-semibold rounded-full hover:bg-brand-yellow-dark transition-colors" data-id="element-274">
                  Done
                </button>
              </motion.div> : <motion.form key="form" onSubmit={handleSubmit} className="space-y-4" data-id="element-275">
                <div className="grid grid-cols-2 gap-4" data-id="element-276">
                  <div data-id="element-277">
                    <label className="block text-xs font-medium text-gray-400 mb-1.5" data-id="element-278">
                      Full Name *
                    </label>
                    <input required type="text" value={form.name} onChange={e => setForm({
                  ...form,
                  name: e.target.value
                })} placeholder="Jane Smith" className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-brand-yellow/50 transition-colors" data-id="element-279" />
                  </div>
                  <div data-id="element-280">
                    <label className="block text-xs font-medium text-gray-400 mb-1.5" data-id="element-281">
                      Email *
                    </label>
                    <input required type="email" value={form.email} onChange={e => setForm({
                  ...form,
                  email: e.target.value
                })} placeholder="jane@company.com" className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-brand-yellow/50 transition-colors" data-id="element-282" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-283">
                  <div data-id="element-284">
                    <label className="block text-xs font-medium text-gray-400 mb-1.5" data-id="element-285">
                      Current Company *
                    </label>
                    <input required type="text" value={form.company} onChange={e => setForm({
                  ...form,
                  company: e.target.value
                })} placeholder="Google, Meta, etc." className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-brand-yellow/50 transition-colors" data-id="element-286" />
                  </div>
                  <div data-id="element-287">
                    <label className="block text-xs font-medium text-gray-400 mb-1.5" data-id="element-288">
                      Job Title *
                    </label>
                    <input required type="text" value={form.role} onChange={e => setForm({
                  ...form,
                  role: e.target.value
                })} placeholder="Senior Product Designer" className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-brand-yellow/50 transition-colors" data-id="element-289" />
                  </div>
                </div>

                <div data-id="element-290">
                  <label className="block text-xs font-medium text-gray-400 mb-1.5" data-id="element-291">
                    LinkedIn Profile
                  </label>
                  <div className="relative" data-id="element-292">
                    <LinkedinIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" data-id="element-293" />
                    <input type="url" value={form.linkedin} onChange={e => setForm({
                  ...form,
                  linkedin: e.target.value
                })} placeholder="linkedin.com/in/yourprofile" className="w-full pl-9 pr-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-brand-yellow/50 transition-colors" data-id="element-294" />
                  </div>
                </div>

                <div data-id="element-295">
                  <label className="block text-xs font-medium text-gray-400 mb-1.5" data-id="element-296">
                    Areas of Expertise *
                  </label>
                  <input required type="text" value={form.expertise} onChange={e => setForm({
                ...form,
                expertise: e.target.value
              })} placeholder="e.g. Figma, UX Research, Design Systems" className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-brand-yellow/50 transition-colors" data-id="element-297" />
                </div>

                <div data-id="element-298">
                  <label className="block text-xs font-medium text-gray-400 mb-1.5" data-id="element-299">
                    Why do you want to mentor? *
                  </label>
                  <textarea required rows={3} value={form.motivation} onChange={e => setForm({
                ...form,
                motivation: e.target.value
              })} placeholder="Tell us what drives you to mentor and what you hope to give back..." className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-brand-yellow/50 transition-colors resize-none" data-id="element-300" />
                </div>

                {/* Benefits reminder */}
                <div className="p-4 rounded-xl bg-brand-yellow/5 border border-brand-yellow/10" data-id="element-301">
                  <p className="text-xs font-semibold text-brand-yellow mb-2" data-id="element-302">
                    What you get as a mentor
                  </p>
                  <ul className="space-y-1.5" data-id="element-303">
                    {['Competitive hourly compensation', 'Build your personal brand & network', 'Access to our mentor community', 'Flexible schedule — mentor on your terms'].map(b => <li key={b} className="flex items-center gap-2 text-xs text-gray-400" data-id="element-304">
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-yellow flex-shrink-0" data-id="element-305" />
                        {b}
                      </li>)}
                  </ul>
                </div>

                <button type="submit" disabled={formState === 'submitting'} className="w-full py-3.5 bg-brand-yellow text-dark-900 font-semibold rounded-xl hover:bg-brand-yellow-dark transition-all duration-200 hover:scale-[1.02] disabled:opacity-70 disabled:scale-100 flex items-center justify-center gap-2" data-id="element-306">
                  {formState === 'submitting' ? <>
                      <motion.span animate={{
                  rotate: 360
                }} transition={{
                  duration: 1,
                  repeat: Infinity,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-dark-900/30 border-t-dark-900 rounded-full" data-id="element-307" />
                      Submitting...
                    </> : <>
                      Submit Application
                      <ArrowRightIcon className="w-4 h-4" data-id="element-308" />
                    </>}
                </button>
              </motion.form>}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>;
}
export function MentorsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [showModal, setShowModal] = useState(false);
  return <>
      <section id="mentors" className="py-24 bg-dark-900 relative overflow-hidden" data-id="element-309">
        {/* Background */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-yellow/5 rounded-full blur-3xl pointer-events-none" data-id="element-310" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" ref={ref} data-id="element-311">
          {/* Section Header */}
          <div className="text-center mb-16" data-id="element-312">
            <motion.span initial={{
            opacity: 0,
            y: 20
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5
          }} className="inline-block text-brand-yellow text-sm font-semibold tracking-wider uppercase mb-4" data-id="element-313">
              World-Class Mentors
            </motion.span>
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-4xl sm:text-5xl font-bold text-white mb-6" data-id="element-314">
              Learn from designers at
              <br data-id="element-315" />
              <span className="text-gradient-yellow" data-id="element-316">
                the world's best companies
              </span>
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg text-gray-400 max-w-2xl mx-auto" data-id="element-317">
              Every student gets matched with a senior mentor for weekly 1-on-1
              sessions. Our mentors have shipped products used by billions of
              people.
            </motion.p>
          </div>

          {/* Mentor Cards */}

          {/* Become a Mentor CTA */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.6
        }} className="relative rounded-2xl overflow-hidden border border-brand-yellow/20 bg-gradient-to-r from-brand-yellow/10 via-brand-yellow/5 to-transparent p-8 md:p-12" data-id="element-318">
            <div className="absolute top-0 right-0 w-64 h-64 bg-brand-yellow/10 rounded-full blur-3xl pointer-events-none" data-id="element-319" />
            <div className="relative flex flex-col md:flex-row items-center justify-between gap-8" data-id="element-320">
              <div className="text-center md:text-left" data-id="element-321">
                <span className="inline-block text-brand-yellow text-sm font-semibold tracking-wider uppercase mb-3" data-id="element-322">
                  Join Our Mentor Network
                </span>
                <h3 className="text-3xl font-bold text-white mb-3" data-id="element-323">
                  Are you a senior designer?
                  <br data-id="element-324" />
                  <span className="text-gradient-yellow" data-id="element-325">Become a mentor.</span>
                </h3>
                <p className="text-gray-400 max-w-lg" data-id="element-326">
                  Share your expertise, earn competitive compensation, and help
                  shape the next generation of world-class designers. We're
                  selective — and that's the point.
                </p>
                <div className="flex flex-wrap gap-6 mt-5 justify-center md:justify-start" data-id="element-327">
                  {[{
                  label: 'Global community',
                  icon: '🌍'
                }].map(item => <div key={item.label} className="flex items-center gap-2 text-sm text-gray-300" data-id="element-328">
                      <span data-id="element-329">{item.icon}</span>
                      <span data-id="element-330">{item.label}</span>
                    </div>)}
                </div>
              </div>
              <div className="flex-shrink-0" data-id="element-331">
                <button onClick={() => setShowModal(true)} className="group inline-flex items-center gap-2 px-8 py-4 text-base font-semibold bg-brand-yellow text-dark-900 rounded-full hover:bg-brand-yellow-dark transition-all duration-300 hover:scale-105 glow-yellow whitespace-nowrap" data-id="element-332">
                  Apply to Mentor
                  <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-333" />
                </button>
                <p className="text-xs text-gray-500 text-center mt-3" data-id="element-334">
                  We review all applications within 5 days
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <AnimatePresence data-id="element-335">
        {showModal && <ApplyModal onClose={() => setShowModal(false)} data-id="element-336" />}
      </AnimatePresence>
    </>;
}